import styled from "@emotion/styled";
import { IonIcon, useIonRouter } from "@ionic/react";
import { barcodeSharp } from "ionicons/icons";
import React, { useEffect, useState } from "react";
import { useWindowSize } from "react-use";
import IconButton from "../IconButton/IconButton";
import { Menu } from "lucide-react";
import LoginModal from "../LoginModal/LoginModal";
import { isMobileDevice } from "../../util/util";
import { CustomThemeType } from "../../context/Providers";
import { useUserData } from "../../context/UserDataFetcher";
import { useAuthUser } from "../../context/UserManager";

const NavBarWrapper = styled.div<{
  theme?: CustomThemeType;
  isDropdownOpen: boolean;
}>`
  background-color: ${(p) =>
    p.isDropdownOpen
      ? `color-mix(in hsl, ${p.theme.background}, ${p.theme.brand} 5%)`
      : p.theme.background};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  padding: 2rem 4rem 0 4rem;
  transition: background-color 0.3s ease;
  & > div:first-of-type {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
  & > div:last-of-type {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
  @media (max-width: 1150px) {
    padding: 0.5rem 0.5rem 0 0.5rem;
  }
`;

const NavBarSection = styled.div<{ theme?: CustomThemeType }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 33%;
  & svg {
    color: ${(p) => p.theme.foreground};
    stroke: ${(p) => p.theme.foreground};
  }
`;

const NavButton = styled.button<{
  theme?: CustomThemeType;
  forceBorder: boolean;
}>`
  background-color: transparent;
  color: ${(p) => p.theme.foreground};
  padding: 1rem;
  margin: 0 1rem;
  border: ${(p) =>
    p.forceBorder
      ? `.1rem solid ${p.theme.foreground}`
      : "1px solid transparent"};
  border-radius: 0.5rem;
  font-family: "Inter";
  font-size: 1.5rem;
  transition: border 0.25s ease;
  &:hover {
    background-color: transparent;
    border: 0.1rem solid ${(p) => p.theme.foreground};
  }
  @media (max-width: 1150px) {
    font-size: 1.2rem;
  }
`;

const LogoButton = styled.button<{ theme?: CustomThemeType }>`
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  &:hover {
    background-color: transparent;
  }
  & span {
    color: ${(p) => p.theme.foreground};
    margin-left: 0.25rem;
  }
  & ion-icon {
    color: ${(p) => p.theme.brand};
  }
`;

const LoginButton = styled.button<{ theme?: CustomThemeType }>`
  background-color: transparent;
  color: ${(p) => p.theme.foreground};
  padding: 1rem;
  border: 0.1rem solid ${(p) => p.theme.foreground};
  border-radius: 0.5rem;
  font-family: "Inter";
  font-size: 1.5rem;
  transition: border 0.25s ease;
  &:hover {
    background-color: transparent;
    border: 1px solid ${(p) => p.theme.foreground};
  }
  @media (max-width: 1150px) {
    font-size: 1.2rem;
  }
`;

const MenuDropdownWrapper = styled.div<{
  theme?: CustomThemeType;
  isDropdownOpen: boolean;
}>`
  background-color: ${(p) =>
    p.isDropdownOpen
      ? `color-mix(in hsl, ${p.theme.background}, ${p.theme.brand} 5%)`
      : p.theme.background};
  opacity: ${(p) => (p.isDropdownOpen ? 1 : 0)};
  transition:
    max-height 0.5s ease,
    background-color 0.3s ease,
    opacity 0.3s ease;
  max-height: ${(p) => (p.isDropdownOpen ? "13rem" : "0px")};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const MenuDropdownButton = styled.button<{ theme?: CustomThemeType }>`
  height: 4rem;
  background-color: transparent;
  color: ${(p) => p.theme.foreground};
  flex-shrink: 0;
  width: 100%;
  &:hover {
    background-color: color-mix(
      in srgb,
      ${(p) => p.theme.background},
      ${(p) => p.theme.foreground} 5%
    );
  }
`;

interface NavBarProps {
  currentTab: string;
  onNavigate: (newTab: string) => any;
  onGetInTouchClick: () => any;
}

const NavBar: React.FC<NavBarProps> = ({
  currentTab,
  onNavigate,
  onGetInTouchClick,
}) => {
  const { width } = useWindowSize();
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const router = useIonRouter();
  const authUser = useAuthUser();

  // useEffect(() => {
  //   console.log('HERE');
  //   console.log(authUser);
  // }, [authUser]);

  const onLoginSuccess = () => {
    setShowModal(false);

    // router.push("/tab2");
    // if (isMobileDevice()) {
    //     router.push('/tab1');
    // } else {
    //     router.push('/desktop-uploadpage');
    // }
  };

  const narrowScreenWidthContent = (
    <>
      <NavBarWrapper isDropdownOpen={isDropdownOpen}>
        <NavBarSection>
          <LogoButton>
            <IonIcon icon={barcodeSharp} />
          </LogoButton>
        </NavBarSection>
        <NavBarSection>
          <IconButton
            onClick={() => {
              setIsDropdownOpen(!isDropdownOpen);
            }}
          >
            <Menu size="2rem" color="black" style={{ marginLeft: "2rem" }} />
          </IconButton>
        </NavBarSection>
      </NavBarWrapper>
      <MenuDropdownWrapper isDropdownOpen={isDropdownOpen}>
        <MenuDropdownButton
          onClick={() => {
            setIsDropdownOpen(false);
            setShowModal(true);
          }}
        >
          Login
        </MenuDropdownButton>
        <MenuDropdownButton
          onClick={() => {
            setIsDropdownOpen(false);
            onNavigate("product");
          }}
        >
          Product
        </MenuDropdownButton>
        <MenuDropdownButton
          onClick={() => {
            setIsDropdownOpen(false);
            onNavigate("contact");
          }}
        >
          Contact
        </MenuDropdownButton>
      </MenuDropdownWrapper>
    </>
  );
  const wideScreenWidthContent = (
    <NavBarWrapper isDropdownOpen={false}>
      <NavBarSection>
        <LogoButton onClick={() => onNavigate("product")}>
          <IonIcon icon={barcodeSharp} />
          {width > 1150 && <span>Cabinet Scanner</span>}
        </LogoButton>
      </NavBarSection>
      <NavBarSection>
        <NavButton
          forceBorder={currentTab === "product"}
          onClick={() => onNavigate("product")}
        >
          Product
        </NavButton>
        <NavButton
          forceBorder={currentTab === "contact"}
          onClick={() => onNavigate("contact")}
        >
          Contact
        </NavButton>
      </NavBarSection>
      <NavBarSection>
        <LoginButton
          onClick={() => {
            setShowModal(true);
          }}
        >
          Login
        </LoginButton>
      </NavBarSection>
    </NavBarWrapper>
  );
  return (
    <>
      {width < 800 ? narrowScreenWidthContent : wideScreenWidthContent}
      <LoginModal
        onGetInTouchClick={() => {
          setShowModal(false);
          onGetInTouchClick();
        }}
        showModal={showModal}
        onLoginSuccess={onLoginSuccess}
        setShowModal={setShowModal}
      />
    </>
  );
};

export default NavBar;
